import KML from "ol/format/KML.js";
import GeoJSON from "ol/format/GeoJSON";
import CircleStyle from "ol/style/Circle";
import { GeometryCollection, LineString, Point, Polygon } from "ol/geom";
import { Colors, GetCoordinatesForSubmissions } from "../../map/mapUtil";
import { Feature } from "ol";
import { Fill, Icon, Stroke, Style } from "ol/style";

const Kml = new KML();
const GeoJson = new GeoJSON();

// Parses an Infrastructure Asset file (KML / GeoJson).
// It will optionally include "name", "type", "assessment", and "description" properties.
export async function ParseInfrastructureFile(file) {
    return new Promise((resolve, reject) => {
        const isKML = /(\.kml|\.kmz)$/i.exec(file.name.toLowerCase());
        const isGeoJson = /(.json)$/i.exec(file.name.toLowerCase());
        if (!isKML && !isGeoJson) {
            return reject("Please choose a valid KML or GeoJson format.");
        } else if (file.size > 500000) {
            return reject("Please choose a file that is less than 500KB.");
        }

        const fileReader = new FileReader();
        fileReader.onloadend = (e) => {
            let features = [];
            if (isKML) {
                const kmlDoc = new DOMParser().parseFromString(e.target.result, "text/xml");
                features = Kml.readFeatures(kmlDoc);
            } else {
                const json = JSON.parse(e.target.result);
                features = GeoJson.readFeatures(json);
            }

            const validFeatures = [];
            features.forEach((feature, i) => {
                const geometry = feature.getGeometry();
                if (
                    geometry &&
                    (geometry instanceof Polygon || geometry instanceof LineString || geometry instanceof Point || geometry instanceof GeometryCollection)
                ) {
                    const fileName = file.name.replace(/\.[^/.]+$/, "");
                    const name = feature.get("name") || fileName + " #" + (i + 1);
                    const assessment = feature.get("assessment");
                    feature.set("coords", [GetCoordinatesForSubmissions(feature.getGeometry()).coordinates[0]]);
                    feature.set("name", name);
                    feature.set("assessment", assessment === true || assessment === "true");
                    validFeatures.push(feature);
                }
            });

            if (validFeatures.length === 0) {
                return reject("Please choose a KML or GeoJson file with at least 1 valid polygon.");
            }

            resolve(validFeatures);
        };
        fileReader.onerror = () => reject("File could not be parsed correctly.");
        fileReader.readAsText(file);
    });
}

// Converts infrastructure assets to features.
export function ConvertInfrastructureAssetsToFeatures(assets, infrastructureTypes, typeFilter) {
    const features = [];
    for (const asset of assets) {
        const type = infrastructureTypes.find((type) => type.id === asset.type_id);
        if (typeFilter !== "All" && typeFilter !== asset.type_id) {
            continue;
        }

        const geometryType = asset.coordinates[0].length === 1 ? "Point" : "Polygon";
        const styles = [];

        if (geometryType === "Polygon") {
            styles.push(
                new Style({
                    fill: new Fill({ color: "rgba(0, 135, 191, 0.05)" }),
                    stroke: new Stroke({ color: "rgba(0, 135, 191, 0.9)", width: 3 })
                })
            );
        } else {
            styles.push(
                new Style({
                    image: new CircleStyle({
                        fill: new Fill({ color: "rgba(0, 135, 191, 0.05)" }),
                        stroke: new Stroke({ color: "rgba(0, 135, 191, 0.9)", width: 3 }),
                        radius: 1
                    })
                })
            );
        }

        if (asset.type_id !== -1 && type) {
            styles.push(
                new Style({
                    image: new Icon({
                        src: `../static/2d-models/infrastructure/${type.name}.svg`,
                        scale: 0.5,
                        color: asset.assessment ? Colors.lime : Colors.red
                    }),
                    geometry: (feature) => {
                        const geometry = feature.getGeometry();
                        if (geometry.getType() === "Polygon") {
                            return geometry.getInteriorPoint();
                        }
                        return geometry;
                    }
                })
            );
        }

        const geometryCoords = geometryType === "Point" ? asset.coordinates[0][0] : asset.coordinates;
        const geometry = GeoJson.readGeometry({ type: geometryType, coordinates: geometryCoords });
        const feature = new Feature(geometry);
        feature.setId(asset.id);
        feature.setStyle(styles);
        features.push(feature);
    }
    return features;
}
